import type { ScheduleSlotListItem } from '../widget-api/schedule-slots';

export type TrainerSlotLayoutMode = 'today' | 'week' | null;

export function isTrainerOwnOrUnassignedSlot(
  slot: ScheduleSlotListItem,
  trainer: { id?: number | null; full_name?: string | null } | null
): boolean {
  const trainerID = trainer?.id ?? null;
  const trainerName = (trainer?.full_name ?? '').trim().toLowerCase();
  const slotTrainerID = slot.trainer_user_id ?? slot.trainer_id ?? null;
  if (slotTrainerID != null && trainerID != null) return slotTrainerID === trainerID;
  if (slot.trainer_name?.trim() && trainerName) return slot.trainer_name.trim().toLowerCase() === trainerName;
  return true;
}

export function pickActiveBookingSlots(
  slots: ScheduleSlotListItem[],
  mode: TrainerSlotLayoutMode,
  desktopVisibleDayEntries: ReadonlyArray<readonly [string, ScheduleSlotListItem[]]>,
  mobileWeekSelectedISO: string,
  todayISO: string
): ScheduleSlotListItem[] {
  if (mode === 'today') return slots.filter((slot) => slot.slot_date === todayISO);
  if (mode === 'week') return slots.filter((slot) => slot.slot_date === mobileWeekSelectedISO);
  return desktopVisibleDayEntries.flatMap(([, daySlots]) => daySlots);
}


export type TrainerSpaceGroup = {
  key: string;
  spaceId: number | null;
  spaceName: string;
  branchId: number | null;
  branchName: string | null;
  slots: ScheduleSlotListItem[];
};

/** Своё занятие: тренер слота — текущий тренер (без «или без тренера»). */
export function isTrainerOwnSlot(
  slot: ScheduleSlotListItem,
  trainer: { id?: number | null; full_name?: string | null } | null
): boolean {
  const trainerID = trainer?.id ?? null;
  const trainerName = (trainer?.full_name ?? '').trim().toLowerCase();
  const slotTrainerID = slot.trainer_user_id ?? slot.trainer_id ?? null;
  if (slotTrainerID != null && trainerID != null) return slotTrainerID === trainerID;
  if (slot.trainer_name?.trim() && trainerName) return slot.trainer_name.trim().toLowerCase() === trainerName;
  return false;
}

/** «Записано N из M»: N — занятые места, M — вместимость слота. */
export function trainerSlotBookedCount(slot: ScheduleSlotListItem): number {
  if (slot.max_participants <= 0) return 0;
  return Math.max(0, slot.max_participants - slot.places_left);
}

/**
 * Группы по пространствам для сетки тренера. Пространство и филиал — части ключа:
 * одинаковые названия залов в разных филиалах не смешиваются. Слоты без space_id —
 * группа «Без пространства».
 */
export function groupTrainerSlotsBySpace(
  slots: ScheduleSlotListItem[],
  branchNames: ReadonlyMap<number, string>
): TrainerSpaceGroup[] {
  const map = new Map<string, TrainerSpaceGroup>();
  for (const slot of slots) {
    const spaceId = slot.space_id ?? null;
    const branchId = slot.branch_id ?? null;
    const key = `${spaceId ?? 'none'}|${branchId ?? 'none'}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        spaceId,
        spaceName: slot.space_name?.trim() || 'Без пространства',
        branchId,
        branchName: branchId != null ? branchNames.get(branchId) ?? `Филиал #${branchId}` : null,
        slots: []
      };
      map.set(key, group);
    }
    group.slots.push(slot);
  }
  const groups = [...map.values()];
  groups.sort((a, b) => {
    const an = a.spaceId != null ? a.spaceName.toLowerCase() : '\uffff';
    const bn = b.spaceId != null ? b.spaceName.toLowerCase() : '\uffff';
    if (an !== bn) return an.localeCompare(bn, 'ru');
    return (a.branchId ?? 0) - (b.branchId ?? 0);
  });
  for (const group of groups) {
    group.slots.sort((a, b) => {
      const byTime = (a.start_time ?? '').localeCompare(b.start_time ?? '');
      return byTime !== 0 ? byTime : a.id - b.id;
    });
  }
  return groups;
}

/** Подпись группы: пространство и филиал, если филиалов несколько. */
export function trainerSpaceGroupTitle(group: TrainerSpaceGroup, multiBranch: boolean): string {
  if (!multiBranch || !group.branchName) return group.spaceName;
  return `${group.spaceName} · ${group.branchName}`;
}
